import { PERTENTO_COMPANY_ID } from './index';

/**
 * Whether server-side experiments are staff-only (PER-178). Opened to every account on
 * 2026-10-26, before general availability, so outside testers can use it; setting it back to
 * true hides it again everywhere, the API included.
 *
 * Flipping it is the only change needed either way, but these tests pin the current state on
 * purpose and fail on a flip, as a tripwire; update them with it:
 * - src/workspaces/definitions/src/server-side-access.test.ts ("ships open to every account")
 * - src/servers/api/experiments/server-settings/update-server-settings.test.ts (customers allowed)
 * - src/servers/api/variants/variant-server-fields.test.ts (customers allowed)
 * - src/servers/api/websites/update-website-settings.test.ts (customers may choose and re-send
 *   'server')
 * - src/servers/api/server-keys/router-authorization.test.ts (customers reach their own website)
 * - src/apps/dashboard/tours/tour-rules.test.ts (the real server-side tour steps show to
 *   a customer)
 * And these branch on it, covering whichever side ships:
 * - src/servers/api/server-keys/create-server-key.test.ts and revoke-server-key.test.ts (the
 *   server key notifications, held back while staff-only, and their payloads when open)
 */
export const SERVER_SIDE_STAFF_ONLY = false;

/**
 * Notifications about server-side changes. While staff-only they are not sent: the website's
 * Owners and Admins would be told about keys they cannot see or revoke (staff get the Discord
 * activity alert instead), and the settings matrix hides them from anyone who cannot use
 * server-side.
 */
export const SERVER_SIDE_NOTIFICATION_TYPES = ['server_key.created', 'server_key.revoked'] as const;

/** What the API answers a non-staff caller who reaches a server-side write. */
export const SERVER_SIDE_UNAVAILABLE = 'Server-side experiments are not available on this account yet';

type ServerSideUser = { role?: string | null; companyId?: number | null } | null | undefined;

type ServerSideAllowedParams = { user: ServerSideUser; staffOnly: boolean };

/**
 * Pertento staff: a Super Admin, or anyone whose own company is Pertento's. Deliberately the
 * user's company, not the website's: the authorization guards already let a Pertento user
 * reach an `underPertento` customer, and this asks a different question, who may see an
 * unreleased feature at all.
 */
export const isPertentoStaff = (user: ServerSideUser): boolean =>
  user?.role === 'Super Admin' || (user?.companyId ?? null) === PERTENTO_COMPANY_ID;

/** The gate with the dark-launch switch passed in, so tests can cover both sides of it. */
export const serverSideAllowed = ({ user, staffOnly }: ServerSideAllowedParams): boolean =>
  !staffOnly || isPertentoStaff(user);

/**
 * Whether this user may switch server-side on: the Assignment card, variant keys and
 * payloads, server keys and the Server-side tracking method. One check for the API and the
 * dashboard alike, so opening it up is flipping SERVER_SIDE_STAFF_ONLY and nothing else.
 */
export const canUseServerSide = (user: ServerSideUser): boolean =>
  serverSideAllowed({ user, staffOnly: SERVER_SIDE_STAFF_ONLY });
